import type { ReactNode } from 'react'
import { cn, getSectionIconColors } from '@/lib/utils'

/** A heading's hue: a `getSectionIconColors` theme, or an entity's own (`SECTION_ACCENTS`, as `{ text, bg }`). */
export type SectionTone = { text: string; bg: string }

export const sectionTone = (theme: Parameters<typeof getSectionIconColors>[0]): SectionTone =>
  getSectionIconColors(theme)

/**
 * The reports' section header (comparison-profiles.tsx and the other report sections), shared: a tinted rounded-xl
 * icon tile, the section's name beside it and a muted line under it, with the section's controls on the right.
 * The tile carries the section's hue; the primary colour stays for actions.
 */
export function SectionHeading(props: {
  /** `null` keeps the tile and leaves the glyph out (a pending shell). */
  icon: React.ElementType | null
  tone: SectionTone
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** The heading's own id, for a section that names itself by it (`aria-labelledby`). */
  id?: string
  className?: string
}) {
  const Icon = props.icon
  return (
    <div className={cn('flex w-full flex-wrap gap-3 sm:flex-nowrap sm:items-center sm:gap-4', props.className)}>
      {/* On a phone the actions wrap under the title rather than squeezing it. */}
      <div className="flex min-w-48 flex-1 flex-col gap-2 sm:min-w-0 sm:gap-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl sm:h-10 sm:w-10',
              props.tone.bg,
            )}
          >
            {Icon && <Icon className={cn('h-4 w-4 shrink-0 sm:h-5 sm:w-5', props.tone.text)} />}
          </div>
          <h2 id={props.id} className="text-foreground min-w-0 text-lg font-semibold sm:text-xl">
            {props.title}
          </h2>
        </div>
        {props.description && <p className="text-muted-foreground text-xs sm:text-sm">{props.description}</p>}
      </div>
      {props.actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{props.actions}</div>}
    </div>
  )
}
